import type {
  CurrencyMapping,
  Transaction,
} from "@/apis/transaction/transaction-apis";
import { useCurrencyMappings, useTransaction } from "@/v2/queries";
import {
  type BaseEntityModalResult,
  createEntityModalHook,
} from "./createEntityModalHook";

const useBaseTransactionModal = createEntityModalHook<Transaction, string>({
  useQueryHook: useTransaction,
  getEntityId: (transaction) => transaction.id,
});

export interface UseTransactionModalResult
  extends Omit<BaseEntityModalResult<Transaction, string>, "entity"> {
  transaction: Transaction | null;
  currencyMappings: CurrencyMapping[];
  isLoadingDropdowns: boolean;
}

/**
 * Hook for managing transaction modal state and data loading.
 *
 * Handles:
 * - Modal visibility state
 * - Loading transaction data via TanStack Query
 * - Loading currency mappings for display
 * - Modal opens as soon as transaction loads (prevents stale cached data)
 */
export function useTransactionModal(): UseTransactionModalResult {
  const baseModal = useBaseTransactionModal();
  const { data: currencyMappings, isLoading: isLoadingMappings } =
    useCurrencyMappings();

  return {
    isOpen: baseModal.isOpen,
    transaction: baseModal.entity,
    isLoading: baseModal.isLoading,
    currencyMappings: currencyMappings ?? [],
    open: baseModal.open,
    close: baseModal.close,
    isLoadingDropdowns: isLoadingMappings,
  };
}
